import React, { PropsWithChildren, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useHeaderSize } from '@/hooks/useHeaderSize';
import { useSmartScale } from '@/hooks/useSmartScale';

type SectionFrameProps = PropsWithChildren<{
  className?: string;
  /** disable scale for sections like hero that already manage responsive typography */
  noScale?: boolean;
  /** disable header offset/padding + use full viewport height (for true full-bleed sections) */
  noOffset?: boolean;
  /** center children vertically and horizontally (default true) */
  center?: boolean;
  /** scaling targets (content design size) */
  targetHeight?: number;
  targetWidth?: number;
  /** custom clamp bounds for scaling */
  minScale?: number;
  maxScale?: number;
  /** optional id for anchor navigation */
  id?: string;
  /** optional style to merge into host container (e.g., background styles) */
  style?: React.CSSProperties;
  /** optional overlay classes for a full-bleed color/gradient overlay at section root */
  overlayClassName?: string;
  /** optional overlay style */
  overlayStyle?: React.CSSProperties;
  /** optional background slot to render full-bleed media (video/image) behind overlay */
  backgroundSlot?: React.ReactNode;
  /** ratio of header offset padding to apply (1 = full header height, 0.8 = 80%) */
  offsetRatio?: number;
  /** pass-through data attributes */
  [key: string]: unknown;
}>;

export function SectionFrame({ className, children, noScale, noOffset, center = false, targetHeight, targetWidth, minScale, maxScale, id, style, overlayClassName, overlayStyle, backgroundSlot, offsetRatio = 1, ...rest }: SectionFrameProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  useHeaderSize();
  // Apply smart scale with sensible defaults to make shrinking more noticeable on small screens
  useSmartScale({
    targetHeight,
    targetWidth,
    min: typeof minScale === 'number' ? minScale : 0.72,
    max: typeof maxScale === 'number' ? maxScale : 1,
  });

  // Ensure CSS vars have sensible defaults
  useEffect(() => {
    const root = document.documentElement;
    if (!root.style.getPropertyValue('--header-h')) root.style.setProperty('--header-h', '64px');
    if (!root.style.getPropertyValue('--section-scale')) root.style.setProperty('--section-scale', '1');
  }, []);

  return (
    <div
      ref={hostRef}
      id={id}
      {...rest}
      className={cn(
        // container ensures header-safe area and scaling context (unless noOffset)
        'relative isolate',
        // inner content scaling can be applied by child wrapper if desired
        className,
      )}
      style={{
        // Robust min-height using app-100svh var with header offset when needed
        minHeight: noOffset ? 'var(--app-100svh)' : 'calc(var(--app-100svh) - var(--header-h))',
        // Apply adjustable header offset padding
        // Use CSS var with a fallback to the provided offsetRatio; allows responsive overrides via className
        paddingTop: noOffset ? undefined : `calc(var(--header-h) * var(--section-offset-ratio, ${offsetRatio}))`,
        // Merge incoming styles (e.g., background styles) after our defaults
        ...style,
      }}
      data-no-scale={noScale ? '' : undefined}
    >
      {/* Full-bleed background slot (e.g., video) */}
      {backgroundSlot && (
        <div className="absolute inset-0 z-0 overflow-hidden" aria-hidden>
          {backgroundSlot}
        </div>
      )}

      {/* Full-bleed overlay at section root (not scaled) */}
      {overlayClassName && (
        <div
          className={cn('absolute inset-0 z-10 pointer-events-none', overlayClassName)}
          style={overlayStyle}
          aria-hidden
        />
      )}
      {/* optional scale wrapper; children can opt-out via noScale */}
      <div
        className={cn(
          'h-full w-full relative z-20',
          center && 'flex items-center justify-center',
          // GPU-accelerated transform for smoother scaling on low-end devices
          noScale ? undefined : 'origin-center transform-gpu scale-[var(--section-scale)] will-change-transform antialiased',
        )}
      >
        {children}
      </div>
    </div>
  );
}

export default SectionFrame;
